import {
  CheckOutlined,
  DirectionsBoatOutlined,
  EmailOutlined,
  PermIdentityOutlined,
} from "@mui/icons-material";
import {
  Button,
  Divider,
  FormControlLabel,
  Grid2,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useNotification, useOne, useParsed, useUpdate } from "@refinedev/core";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { EditView } from "../../components/EditView";
import { Info } from "../../components/ports/info-card";
import { AdminUserDto } from "../../types/models/AdminUserDto";
import { getPrettyFeatureFlagName } from "../../utils/prettyFeatureFlagName";
import { FEATURE_FLAGS } from "../../utils/supportedFeatureFlags";
import { SectionBox } from "../ports/edit";

const initializeMetadata = (
  userMetadata?: { key: string; value: string }[]
) => {
  const defaultMetadataMap = new Map(
    Object.keys(FEATURE_FLAGS).map((key) => [key, ""])
  );

  userMetadata?.forEach(({ key, value }) => {
    if (value !== "") {
      defaultMetadataMap.set(key, value);
    }
  });

  return Array.from(defaultMetadataMap.entries()).map(([key, value]) => ({
    key,
    value,
  }));
};

export const EditUser = () => {
  const { id } = useParsed();

  const { open } = useNotification();

  const {
    result: user,
    query: { isLoading },
  }= useOne<AdminUserDto>({
    resource: "AdminUsers",
    id,
  });

  const { mutate, mutation } = useUpdate<AdminUserDto>({
    resource: "AdminUsers",
    id,
    successNotification: (data, values) => {
      const count = values?.values?.Metadata?.length ?? 0;
      const description = `Updated with ${count} value${
        count === 1 ? "" : "s"
      }.`;

      return {
        message: `The user was successfully updated.`,
        description: description,
        type: "success",
      };
    },
    errorNotification: () => {
      return {
        message: `Something went wrong. Please check console.`,
        description: "An error happened while updating the user.",
        type: "error",
      };
    },
  });

  const metadata = initializeMetadata(user?.metadata);

  const withDefaultMetadata = { ...user, metadata };
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm({
    resetOptions: {
      keepDefaultValues: true,
    },
    values: withDefaultMetadata,
  });

  useEffect(() => {
    if (user) {
      const metadata = initializeMetadata(user.metadata);
      reset({ ...user, metadata });
    }
  }, [user, reset]);

  const onFinishHandler = (data: FieldValues) => {
    if (!isDirty) {
      open?.({
        type: "error",
        message: "Please update the fields to make changes.",
        description: "No changes were detected in the user information.",
      });
      return;
    }

    const changes = dirtyFields
      .metadata!.map((_, i) => {
        const key = data.metadata[i]?.key;
        const value = data.metadata[i]?.value;

        if (key && value != null) {
          return `${key}:${value}`;
        }
        return null;
      })
      .filter((item) => item !== null);

    mutate({ values: { Metadata: changes } });
  };

  return (
    <EditView name="User">
      <Grid2
        size={{ xs: 12, lg: 8 }}
        spacing={2}
        direction={"column"}
        container
      >
        <SectionBox>
          <Info
            icon={<DirectionsBoatOutlined />}
            label={"Vessel IMO"}
            value={user?.vesselImo}
          />

          <Divider />

          <Info
            icon={<EmailOutlined />}
            label={"Auth0 Email"}
            value={user?.email}
          />

          <Divider />

          <Info
            icon={<PermIdentityOutlined />}
            label={"Auth0 ID"}
            value={user?.id}
          />
        </SectionBox>
      </Grid2>
      <Grid2
        size={{ xs: 12, lg: 4 }}
        spacing={2}
        direction={"column"}
        container
      >
        <SectionBox>
          <form onSubmit={handleSubmit(onFinishHandler)}>
            <Typography variant="h6" sx={{ marginBottom: "24px" }}>
              Feature Flags
            </Typography>

            <Stack spacing={2}>
              {(withDefaultMetadata?.metadata ?? []).map(({ key }, index) => {
                const type = FEATURE_FLAGS?.[key];

                if (!type) return null;

                return (
                  <Controller
                    key={index}
                    name={`metadata.${index}.value`}
                    control={control}
                    render={({ field }) =>
                      type === "date" ? (
                        <DatePicker
                          {...field}
                          views={["month", "year"]}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(date ? date.format("YYYY-MM") : "")
                          }
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Switch
                              {...field}
                              checked={field.value === "true"}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked ? "true" : "false"
                                )
                              }
                            />
                          }
                          label={getPrettyFeatureFlagName(key)}
                          style={{ textTransform: "capitalize" }}
                        />
                      )
                    }
                  />
                );
              })}
              <Button
                variant="outlined"
                startIcon={<CheckOutlined />}
                type="submit"
                disabled={!isDirty || isLoading || mutation.isPending}
              >
                Save
              </Button>
            </Stack>
          </form>
        </SectionBox>
      </Grid2>
    </EditView>
  );
};
